import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Gamepad2, Car } from 'lucide-react';
import { MaskLine, FadeUp, EASE } from '@/components/Reveal';
import { DubaiClock } from '@/components/DubaiClock';
import { Marquee } from '@/components/Marquee';
import { Founder } from '@/components/Founder';
import { ORIGIN, PEOPLE, PAST_BRANDS } from '@/data/about';
import { CONTACT } from '@/data/content';

const Obsession = ({ icon: Icon, label, text, testid, delay }) => (
  <FadeUp delay={delay} className="h-full">
    <div data-testid={testid} className="h-full bg-cream border border-ink rounded-[1.5rem] p-7 sm:p-9 shadow-[6px_6px_0_#1A1A1A]">
      <div className="w-12 h-12 rounded-full bg-butter border border-ink flex items-center justify-center mb-6"><Icon size={20} /></div>
      <p className="text-[11px] uppercase tracking-[0.25em] text-royal font-bold mb-3">{label}</p>
      <p className="text-ink/80 leading-relaxed">{text}</p>
    </div>
  </FadeUp>
);

export default function About() {
  return (
    <main data-testid="about-page" className="bg-cream text-ink">
      {/* HERO */}
      <section className="px-6 sm:px-12 pt-40 pb-16 sm:pb-24">
        <div className="max-w-[1500px] mx-auto">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.7, ease: EASE }} className="flex flex-wrap items-center gap-6 mb-8">
            <p className="text-[11px] uppercase tracking-[0.3em] text-royal font-bold">About Us — Dubai, 360° Content Studio</p>
            <DubaiClock />
          </motion.div>
          <h1 data-testid="about-headline" className="font-slab font-black leading-[0.98] tracking-tight text-[11vw] sm:text-[8.5vw] lg:text-[6.5vw]">
            <MaskLine delay={0.2}>THE PEOPLE</MaskLine>
            <span className="relative inline-block">
              <MaskLine delay={0.34}>BEHIND</MaskLine>
              <motion.span
                initial={{ opacity: 0, y: 20, rotate: -8 }}
                animate={{ opacity: 1, y: 0, rotate: -4 }}
                transition={{ delay: 1.05, duration: 0.7, ease: EASE }}
                className="hidden sm:block font-script text-royal absolute top-[0.12em] right-[-2.9em] text-[0.62em] pointer-events-none select-none whitespace-nowrap"
              >
                two brothers
              </motion.span>
            </span>
            <MaskLine delay={0.48}><span className="text-royal">THE BUTTER.</span></MaskLine>
          </h1>
        </div>
      </section>

      {/* ORIGIN */}
      <section data-testid="about-origin" className="px-6 sm:px-12 pb-28">
        <div className="max-w-[1500px] mx-auto">
          <FadeUp>
            <p className="font-slab font-bold text-2xl sm:text-3xl lg:text-4xl leading-snug max-w-4xl mb-14">{ORIGIN.intro}</p>
          </FadeUp>
          <div className="grid md:grid-cols-2 gap-6 mb-16">
            <Obsession icon={Gamepad2} testid="origin-hussein" label={ORIGIN.hussein.label} text={ORIGIN.hussein.text} delay={0} />
            <Obsession icon={Car} testid="origin-burhanuddin" label={ORIGIN.burhanuddin.label} text={ORIGIN.burhanuddin.text} delay={0.1} />
          </div>
          <FadeUp className="max-w-4xl">
            <p className="text-lg sm:text-2xl text-ink/80 leading-relaxed">{ORIGIN.instinct}</p>
            <p data-testid="origin-punch" className="font-script text-royal text-4xl sm:text-5xl mt-6 -rotate-1 inline-block">{ORIGIN.punch}</p>
          </FadeUp>
        </div>
      </section>

      {/* FOUNDERS */}
      <section data-testid="about-founders" className="mx-3 sm:mx-6 bg-ink text-cream rounded-[2.5rem] px-6 sm:px-12 py-24 sm:py-32">
        <div className="max-w-[1500px] mx-auto space-y-28 sm:space-y-40">
          {PEOPLE.map((p, i) => <Founder key={p.id} person={p} flip={i % 2 === 1} />)}
        </div>
      </section>

      {/* PAST BRANDS */}
      <section className="py-20">
        <FadeUp className="px-6 sm:px-12 mb-8">
          <p className="text-[11px] uppercase tracking-[0.3em] text-royal font-bold text-center">Brands we’ve worked on before Butter</p>
        </FadeUp>
        <Marquee
          items={PAST_BRANDS}
          testid="about-brands-marquee"
          duration={36}
          className="bg-butter border-y border-ink py-4 font-slab font-bold uppercase tracking-wider text-xl sm:text-2xl text-ink"
        />
      </section>

      {/* CTA */}
      <section data-testid="about-cta" className="px-6 sm:px-12 pb-28">
        <FadeUp className="max-w-[1500px] mx-auto">
          <div className="bg-butter border border-ink rounded-[2rem] p-10 sm:p-16 flex flex-col lg:flex-row lg:items-end justify-between gap-10 shadow-[10px_10px_0_#1A1A1A]">
            <div>
              <p className="font-script text-royal text-3xl sm:text-4xl mb-3 -rotate-2 inline-block">got a story?</p>
              <h2 className="font-slab font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight">Let’s make it stick.</h2>
              <p className="mt-5 text-ink/75 text-lg max-w-xl">Reach out and let’s figure out how we can work together.</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 lg:items-end">
              <a data-testid="about-cta-email" href={`mailto:${CONTACT.email}`} className="font-slab font-bold text-lg border-b-2 border-ink pb-0.5 hover:text-royal hover:border-royal transition-colors self-start sm:self-center">{CONTACT.email}</a>
              <Link to="/contact" data-testid="about-cta-start" className="group inline-flex items-center gap-2 bg-ink text-cream font-slab font-bold px-7 py-4 rounded-full hover:bg-royal transition-colors self-start">
                Start a Project <ArrowUpRight size={18} className="group-hover:rotate-45 transition-transform duration-300" />
              </Link>
            </div>
          </div>
        </FadeUp>
      </section>
    </main>
  );
}
